import { ExternalLink } from 'lucide-react';
import { useId, type ReactElement } from 'react';
import { Button } from '@/components/Button';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';

export interface MobileParityNoticeProps {
  readonly kind:
    | 'automation'
    | 'custom-fields'
    | 'workspace-admin'
    | 'power-ups'
    | 'view'
    | 'card-share';
  readonly title: string;
  readonly body?: string | undefined;
  readonly href: string;
}

type MobileParityKind = MobileParityNoticeProps['kind'];

/**
 * FE-80.1 — `KIND_LABELS` and `DEFAULT_BODY` used to be module constants, and a
 * module constant cannot call `t()`: it is built once, before any provider
 * exists, so it would freeze the language the module was first imported under.
 * Each table is now built inside a function that takes the translator, from
 * LITERAL `t('…')` calls — literal so the `t('…')` anchor every routing pin in
 * this repository reads can see all ten keys, and `Record<MobileParityKind, …>`
 * so a sixth `kind` is a `tsc` failure rather than a silently missing arm.
 */
function mobileParityKindLabel(
  t: LocaleFormattingTranslate,
  kind: MobileParityKind,
): string {
  const labels: Record<MobileParityKind, string> = {
    automation: t('boardDashboard.mobile.kind.automation'),
    'custom-fields': t('boardDashboard.mobile.kind.customFields'),
    'workspace-admin': t('boardDashboard.mobile.kind.workspaceAdmin'),
    'power-ups': t('boardDashboard.mobile.kind.powerUps'),
    view: t('boardDashboard.mobile.kind.view'),
    'card-share': t('boardDashboard.mobile.kind.cardShare'),
  };
  return labels[kind];
}

function mobileParityDefaultBody(
  t: LocaleFormattingTranslate,
  kind: MobileParityKind,
): string {
  const bodies: Record<MobileParityKind, string> = {
    automation: t('boardDashboard.mobile.body.automation'),
    'custom-fields': t('boardDashboard.mobile.body.customFields'),
    'workspace-admin': t('boardDashboard.mobile.body.workspaceAdmin'),
    'power-ups': t('boardDashboard.mobile.body.powerUps'),
    view: t('boardDashboard.mobile.body.view'),
    'card-share': t('boardDashboard.mobile.body.cardShare'),
  };
  return bodies[kind];
}

export function MobileParityNotice({
  kind,
  title,
  body,
  href,
}: MobileParityNoticeProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const titleId = useId();
  const bodyId = `${titleId}-body`;

  return (
    <section
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className="rounded-card border border-border-strong bg-surface-raised p-4 text-sm text-ink shadow-card"
    >
      <p className="text-xs font-medium uppercase text-ink-muted">
        {mobileParityKindLabel(t, kind)}
      </p>
      {/*
       * FE-81.2 (fan-out 16) — `basis-56` IS THE DECLARATION'S FLOOR, AND
       * WITHOUT IT `flex-wrap` NEVER FIRES.
       *
       * `flex-wrap` breaks a line by comparing each item's HYPOTHETICAL main
       * size — its flex-basis — against the space left. With `basis-auto` the
       * message column's basis is its content, `min-w-0` removes the
       * min-content floor that would otherwise force the break, and the row
       * therefore stays one line at EVERY width: the column shrinks instead of
       * the button moving. The wrap is written down and never happens.
       *
       * Measured off the committed 360×800 frame
       * (`docs/design/fe16-mobile-parity-declarations/fe16-mobile-parity-custom-fields-360x800.png`,
       * reproducible with PIL: the notice's borders sit at x=53 and x=347, the
       * button's at x=206 and x=330). Inside the board menu panel the notice is
       * 294 px wide → 260 px of content, and the split was:
       *
       *   message 124 px  ·  gap 12 px  ·  Open on web 124 px
       *
       * — 48% of the row for the sentence the requirement exists to make
       * readable. `FR-EXP-032`'s custom-fields declaration wrapped to FOUR
       * lines in a 124 px ribbon ("Custom field / setup is / available on the /
       * web") beside an action that had taken half the notice.
       *
       * `basis-56` (14rem = 224 px) states the rule instead: the message keeps
       * at least a 14 rem measure while it shares the row, or the action takes
       * its own row. At 260 px of content 224 + 12 + 124 = 360 > 260, so the
       * button wraps below and the message regains all 260 px. On the board
       * face at 1440×900 the sum fits many times over, the row stays exactly as
       * it was, and the desktop frames do not move.
       *
       * ⚠ `min-w-0` STAYS. `basis-56` raises the hypothetical size, not the
       * floor; a container narrower than 224 px must still be allowed to shrink
       * the column rather than overflow the card. Removing `min-w-0` here
       * trades a cramped measure for a horizontally scrolling notice.
       *
       * ⚠ A viewport breakpoint would be the wrong tool. This notice renders
       * both full-width on the board face and inside a ~294 px menu panel at
       * the same viewport width, so `sm:` would answer a question nobody asked;
       * `flex-basis` is measured against the container the notice is actually
       * in.
       */}
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-56">
          <h2 id={titleId} className="text-base font-semibold text-ink">
            {title}
          </h2>
          <p id={bodyId} className="mt-1 text-ink-muted">
            {body ?? mobileParityDefaultBody(t, kind)}
          </p>
        </div>
        <Button
          as="a"
          href={href}
          size="sm"
          variant="secondary"
          iconStart={<ExternalLink className="size-3.5" />}
        >
          {t('boardDashboard.mobile.openOnWeb')}
        </Button>
      </div>
    </section>
  );
}
